import { useEffect, useRef, useState } from "react";
import { Menu, X, Square } from "lucide-react";

type TerminalPart = {
  t: string;
  c?: "prompt" | "key" | "ok" | "muted" | "cursor";
};

type TerminalLine =
  | { type: "line"; parts: TerminalPart[] }
  | { type: "blank" };

type DingTerminalProps = {
  sequenceName?: "development" | "research";
  loop?: boolean;
};

function mixed(parts: TerminalPart[]): TerminalLine {
  return { type: "line", parts };
}

function blank(): TerminalLine {
  return { type: "blank" };
}

function command(command: string, args: string): TerminalLine {
  return mixed([
    { t: "> ", c: "prompt" },
    { t: command + " ", c: "prompt"},
    { t: args }
  ]);
}



function output(text: string, c?: TerminalPart["c"]): TerminalLine {
  return mixed([
    { t: " ", c: "prompt" },
    { t: text, c },
  ]);
}

function promptCursor(): TerminalLine {
  return mixed([
    { t: "> ", c: "prompt" },
    { t: "_", c: "cursor" },
  ]);
}

const terminalSequences: Record<"development" | "research", TerminalLine[]> = {
  development: [
    command("d!ng", "init --project flies"),
    blank(),
    output("creating project directory      ✅", "muted"),
    output("initializing configuration      ✅", "muted"),
    output("setting up project structure    ✅", "muted"),
    output("scaffolding services            ✅", "muted"),
    output("setting up database             ✅", "muted"),
    output("configuring services            ✅", "muted"),
    output("configuring networking          ✅", "muted"),
    output("initializing git repository     ✅", "muted"),
    output("installing dependencies         ✅", "muted"),
    output("project initialized             ✅", "muted"),
    blank(),
    output("PROJECT SUMMARY", "ok"),
    output("---------------", "ok"),
    output("name:                           flies"),
    output("path:                           ~/src/flies"),
    output("configuration:                  ~/src/flies/config/ding.toml"),
    output("services:                       api, auth, worker, scheduler, gateway, redis, db"),
    output("database:                       configured"),
    output("networking:                     enabled"),
    output("repository:                     initialzed"),
    output("git branch:                     main"),
    output("environment:                    development"),
    blank(),
    output("project flies created successfully", "ok"),
    blank(),
    
    command("cd", "~/src/flies"),
    blank(),

    command("d!ng", "build --config release --target all"),
    blank(),
    output("configuring build             97ms", "muted"),
    output("resolving dependencies        182ms", "muted"),
    output("generating build files        143ms", "muted"),
    output("compiling sources             23.41s", "muted"),
    output("linking targets               6.73s", "muted"),
    output("optimizing binaries           1.93s", "muted"),
    output("running unit tests            4.12s", "muted"),
    output("packaging artifacts           812ms", "muted"),
    output("build completed               32.42s", "muted"),
    blank(),
    output("BUILD SUMMARY", "ok"),
    output("----------------------------------------", "ok"),
    output("TARGET      STATUS    TYPE      SIZE", "ok"),
    output("----------  ------    -------   --------", "ok"),
    output("api         built     service   12.4 MB"),
    output("auth        built     service   8.7 MB"),
    output("worker      built     service   9.1 MB"),
    output("scheduler   built     service   6.3 MB"),
    output("gateway     built     service   5.2 MB"),
    output("redis       built     service   14.6 MB"),
    output("db          built     service   7.8 MB"),
    output("cli         built     binary    2.3MB"),
    output("shared      built     library   1.8MB"),
    blank(),
    output("all targets built successfully", "ok"),
    output("artifacts available in ~/src/flies/build/release", "cursor"),
    blank(),

    command("d!ng", "services up"),
    blank(),
    output("starting local service stack", "muted"),
    output("pulling service images          ✅", "muted"),
    output("loading service configuration   ✅", "muted"),
    output("validating environment          ✅", "muted"),
    output("creating network                ✅", "muted"),
    output("starting services               ✅", "muted"),
    blank(),
    output("SERVICE       STATUS       PORT(S)", "ok"),
    output("----------    ----------   -------", "ok"),
    output("api           listening    8080"),
    output("auth          listening    8081"),
    output("db            healthy      5432"),
    output("redis         healthy      6379"),
    output("scheduler     active       8200"),
    output("worker        subscribed   8100"),
    output("gateway       routing      9000"),
    blank(),
    output("services are up and running. follow logging output:", "ok"),
    output("d!ng services logs -f", "ok"),
    blank(),

    command("d!ng", "deploy --env development"),
    blank(),
    output("validating configuration        ✅", "muted"),
    output("checking git status             ✅", "muted"),
    output("building artifacts              ✅", "muted"),
    output("updating infrastructure         ✅", "muted"),
    output("pushing docker images           ✅", "muted"),
    output("deploying services              ✅", "muted"),
    output("running tests                   ✅", "muted"),
    output("verifying deployment            ✅", "muted"),
    blank(),
    output("SERVICE       STATUS       VERSION", "ok"),
    output("----------    ---------    --------", "ok"),
    output("api           deployed     v1.24.3"),
    output("auth          deployed     v1.18.7"),
    output("db            deployed     v2.5.1"),
    output("redis         deployed     v1.8.2"),
    output("scheduler     deployed     v1.6.4"),
    output("worker        deployed     v13.2.1"),
    output("gateway       deployed     v7.2.4"),
    blank(),
    output("deployment complete", "ok"),
    output("view live: https://flies.dev.dingtech.dev", "ok"),
    blank(),

    command("d!ng", "net trace --env development --session live"),
    blank(),
    output("attaching to network layer      ✅", "muted"),
    output("capturing packets               ✅", "muted"),
    output("decoding protocol frames        ✅", "muted"),
    output("tracking connections            ✅", "muted"),
    output("collecting latency samples      ✅", "muted"),
    output("analyzing packet loss           ✅", "muted"),
    blank(),
    output("trace complete", "ok"),
    output("  duration                      37s"),
    output("  packets                       12,842"),
    output("  connections                   6"),
    output("  tick rate                     60hz"),
    output("  p50 latency                   18ms"),
    output("  p95 latency                   41ms"),
    output("  packet loss                   0.02%"),
    output("  trace status                  clean"),
    output("  output                        trace.log"),
    blank(),
    output("ready for analysis:", "ok"),
    output("d!ng net analyze trace.log", "ok"),
    blank(),

    command("d!ng", "deploy --env production"),
    blank(),
    output("validating configuration        ✅", "muted"),
    output("preflight checks                ✅", "muted"),
    output("updating infrastructure         ✅", "muted"),
    output("canary rollout                  ✅", "muted"),
    output("observability gates             ✅", "muted"),
    output("production deploy               ✅", "muted"),
    output("verifying deployment            ✅", "muted"),
    blank(),
    output("SERVICE       STATUS       VERSION", "ok"),
    output("----------    ---------    --------", "ok"),
    output("api           deployed     v1.24.3"),
    output("auth          deployed     v1.18.7"),
    output("db            deployed     v2.5.1"),
    output("redis         deployed     v1.8.2"),
    output("scheduler     deployed     v1.6.4"),
    output("worker        deployed     v13.2.1"),
    output("gateway       deployed     v7.2.4"),
    blank(),
    output("deployment complete", "ok"),
    output("view live: https://flies.dingtech.dev", "ok"),
    blank(),

    command("d!ng", "services down --networks --volumes"),
    blank(),
    output("loading service configuration     ✅", "muted"),
    output("draining connections              ✅", "muted"),
    output("stopping services                 ✅", "muted"),
    output("removing networks                 ✅", "muted"),
    output("removing volumes                  ✅", "muted"),
    output("cleaning up resources             ✅", "muted"),
    blank(),
    output("SERVICE       STATUS       DETAILS", "ok"),
    output("---------     -------      ------------------------", "ok"),
    output("api           stopped      connections drained (12)"),
    output("auth          stopped      connections drained (4)"),
    output("db            stopped      connections drained (1)"),
    output("redis         stopped      cache flushed (0.8 MB)"),
    output("scheduler     stopped      jobs cancelled (1)"),
    output("worker        stopped      jobs cancelled (2)"),
    output("gateway       stopped      connections drained (13)"),
    blank(),
    output("all services stopped", "ok"),
    output("resources cleaned up", "ok"),
    output("environment is clean", "ok"),
    blank(),

    promptCursor(),
  ],

  research: [
    command("d!ng", "list research.focus"),
    blank(),
    output("querying research focus registry...", "muted"),
    output("loaded 4 active areas", "muted"),
    blank(),
    mixed([{ t: " research.focus", c: "prompt" }]),
    mixed([{ t: " ────────────────────", c: "prompt" }]),
    mixed([{ t: "  ai_local_llms", c: "key" }, { t: "              running and evaluating local language models" }]),
    mixed([{ t: "  ai_retrieval_systems", c: "key" }, { t: "       embeddings, context, chunking, retrieval, evaluation" }]),
    mixed([{ t: "  ai_orchestration", c: "key" }, { t: "           tools, mcp, agents, state, service composition" }]),
    mixed([{ t: "  ai_intelligent_services", c: "key" }, { t: "    backend-integrated AI systems" }]),
    blank(),
    output("research focus registry: OK", "ok"),
    blank(),
    
    promptCursor(),
  ],
};

export function DingTerminal({
  sequenceName = "development",
  loop = true,
}: DingTerminalProps) {
  const outRef = useRef<HTMLDivElement | null>(null);
  const screenRef = useRef<HTMLDivElement | null>(null);
  const shouldAutoScrollRef = useRef(true);
  const [interactive, setInteractive] = useState(false);
  const [sequence, setSequence] = useState(sequenceName)

  useEffect(() => {
    let cancelled = false;

    const scrollToBottomIfFollowing = () => {
      const screen = screenRef.current;
      if (!screen) return;
      if (shouldAutoScrollRef.current) {
        screen.scrollTop = screen.scrollHeight;
      }
    };

    const sleep = (ms: number) =>
      new Promise((resolve) => window.setTimeout(resolve, ms));

    const appendSpan = (
      parent: HTMLElement,
      text: string,
      className?: string
    ) => {
      const span = document.createElement("span");
      if (className) span.className = className;
      span.textContent = text;
      parent.appendChild(span);
    };

    const playOnce = async () => {
      const root = outRef.current;
      if (!root) return;

      for (const item of terminalSequences[sequence]) {
        if (cancelled) return;

        const line = document.createElement("div");
        line.className = "ding-terminal-line";
        root.appendChild(line);
        scrollToBottomIfFollowing();

        if (item.type === "blank") {
          await sleep(180);
          continue;
        }

        for (const part of item.parts) {
          if (part.c === "cursor") {
            appendSpan(line, "", "ding-terminal-cursor");
            continue;
          }

          for (const char of part.t) {
            if (cancelled) return;
            appendSpan(
              line,
              char,
              part.c ? `ding-terminal-${part.c}` : undefined
            );
            
            scrollToBottomIfFollowing()
            
            await sleep(18 + Math.random() * 18);
          }
        }

        await sleep(140);
      }
    };

    const run = async () => {
      await sleep(350);

      do {
        if (!outRef.current || cancelled) return;
        shouldAutoScrollRef.current = true;
        outRef.current.innerHTML = "";
        await playOnce();
        await sleep(3200);
      } while (loop && !cancelled);
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [sequence, loop]);

  return (
  <section className="ding-terminal-shell" aria-label="d!ng animated terminal output">
    <div className="ding-terminal-titlebar">
      <div className="ding-terminal-window-controls" aria-hidden="true"
        onClick={() => {
          if (sequence == "research") {
            setSequence("development");
          } else {
            setSequence("research");
          }
        }}
      >
        <span><Menu size={16} /></span>
      </div>

      <div className="ding-terminal-title">todd@io: ~/src</div>

    
      <div className="ding-terminal-title-actions" aria-hidden="true">
        <span>__</span>
        <span><Square size={16} /></span>
        <span><X size={16}/></span>
      </div>
    </div>

    <div
      ref={screenRef}
      className={
        interactive
          ? "ding-terminal-screen ding-terminal-screen--interactive"
          : "ding-terminal-screen"
      }
      tabIndex={0}
      onClick={() => setInteractive(true)}
      onFocus={() => setInteractive(true)}
      onMouseLeave={() => setInteractive(false)}
      onScroll={(event) => {
        const el = event.currentTarget;
        const distanceFromBottom =
          el.scrollHeight - el.scrollTop - el.clientHeight;

        shouldAutoScrollRef.current = distanceFromBottom < 24;
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          setInteractive(false);
          event.currentTarget.blur();
        }
      }}
    >
      <div ref={outRef} />
    </div>
  </section>
);
}